interface CompositionOption {
  id: string;
  name: string;
  description: string;
  channel: string;
  groupName?: string | null;
}

interface TemplateGroupSelectorProps {
  groupNames: string[];
  selectedGroups: string[];
  onToggleGroup: (group: string) => void;
  availableCompositions: CompositionOption[];
  selectedTemplates: string[];
  onToggleTemplate: (name: string) => void;
  customMode: boolean;
  onToggleMode: () => void;
  loading: boolean;
  channelAvailable: boolean;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-4">
      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Carregando...</span>
    </div>
  );
}

function GroupList({ groupNames, selectedGroups, onToggleGroup, availableCompositions }: Pick<TemplateGroupSelectorProps, 'groupNames' | 'selectedGroups' | 'onToggleGroup' | 'availableCompositions'>) {
  const countFor = (group: string) => availableCompositions.filter((c) => c.groupName === group).length;
  return (
    <div className="space-y-2">
      {groupNames.map(group => (
        <label key={group} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 p-2 rounded">
          <input
            type="checkbox"
            checked={selectedGroups.includes(group)}
            onChange={() => onToggleGroup(group)}
            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-slate-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">{group}</span>
          <span className="text-xs text-gray-400">({countFor(group)} composições)</span>
        </label>
      ))}
    </div>
  );
}

function CompositionList({ availableCompositions, selectedTemplates, onToggleTemplate }: Pick<TemplateGroupSelectorProps, 'availableCompositions' | 'selectedTemplates' | 'onToggleTemplate'>) {
  if (availableCompositions.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma composição disponível para este canal</p>;
  }
  return (
    <div className="space-y-2">
      {availableCompositions.map(composition => (
        <label key={composition.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 p-2 rounded">
          <input
            type="checkbox"
            checked={selectedTemplates.includes(composition.id) || selectedTemplates.includes(composition.name)}
            onChange={() => onToggleTemplate(composition.id)}
            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-slate-700"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-sm text-gray-700 dark:text-gray-300 truncate">{composition.description || composition.name}</span>
            <span className="block text-xs text-gray-400 truncate">{composition.name}</span>
          </span>
          {composition.groupName && (
            <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs text-gray-500">{composition.groupName}</span>
          )}
        </label>
      ))}
    </div>
  );
}

function SelectorContent(props: TemplateGroupSelectorProps) {
  if (props.loading) return <LoadingState />;
  if (!props.channelAvailable) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Este ponto de contato não recebe composições.</p>;
  }
  if (!props.customMode) return <GroupList {...props} />;
  return <CompositionList {...props} />;
}

export default function TemplateGroupSelector(props: TemplateGroupSelectorProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {props.customMode ? 'Modelos de notificação' : 'Grupos de notificação'}
        </label>
        {props.channelAvailable && (
          <button type="button" onClick={props.onToggleMode} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {props.customMode ? 'Selecionar por grupo' : 'Personalizar'}
          </button>
        )}
      </div>
      <div className="border border-gray-300 dark:border-slate-600 rounded-lg p-3 max-h-40 overflow-y-auto">
        <SelectorContent {...props} />
      </div>
    </div>
  );
}
